import { useMemo } from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DEFAULT_SHORTCUTS } from 'shared/mui/DatePicker/components/DatePickerShortcuts/constants/shortcuts';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Row } from 'shared/mui/Row/Row';
import { BooleanInput, SelectInput, TextInput } from 'shared/react-admin/Inputs';

export type FilterSaveFavoriteDialogFormValues = {
  name: string;
  period: string;
  favorite: boolean;
};

type FilterSaveFavoriteDialogProps = {
  onSubmit: (values: FilterSaveFavoriteDialogFormValues) => void;
} & DialogProps;

export const FilterSaveFavoriteDialog: React.FC<FilterSaveFavoriteDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const translate = useTypedTranslate();

  const form = useForm<FilterSaveFavoriteDialogFormValues>({
    mode: 'onSubmit',
  });

  const periodChoices = useMemo(() => {
    return DEFAULT_SHORTCUTS(DEFAULT_TIMEZONE)
      .flat(1)
      .map((shortcut) => ({ label: shortcut.label, value: shortcut.alias }));
  }, []);

  const onSubmitHandler = async (values: FilterSaveFavoriteDialogFormValues) => {
    onSubmit(values);
    form.reset({});
    onClose();
  };

  const onReset = () => {
    form.reset({ period: undefined, name: '' });
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <Card sx={{ width: '600px' }}>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.buttons.saveFilter')}
        />
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmitHandler)}>
            <CardContent>
              <Row marginBottom={2}>
                <TextInput
                  fullWidth={true}
                  label={translate('app.table.name')}
                  name="name"
                  size="medium"
                  source="name"
                  validate={required()}
                  variant="outlined"
                />
              </Row>
              <Row>
                <SelectInput
                  choices={periodChoices}
                  defaultValue={null}
                  fullWidth
                  helperText={false}
                  label={translate('app.filter.defaultDateRange')}
                  optionText="label"
                  optionValue="value"
                  size="medium"
                  source="period"
                  validate={required()}
                  variant="outlined"
                />
              </Row>
              <BooleanInput
                label={translate('app.filter.markAsFavourite')}
                source="favorite"
                sx={{ marginTop: '8px' }}
              />
            </CardContent>
            <CardActionsButtons
              leftActionsSlot={[
                <Button
                  disabled={!form.formState.isDirty}
                  key="save-button"
                  label={translate('app.buttons.save')}
                  type="submit"
                  variant="contained"
                />,
                <CancelButton
                  key="cancel-button"
                  label={translate('app.buttons.reset')}
                  onClick={onReset}
                  variant="outlined"
                />,
              ]}
            />
          </form>
        </FormProvider>
      </Card>
    </Dialog>
  );
};
